import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import UltimatePagination from 'react-ultimate-pagination-bootstrap-4';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faUsers } from '@fortawesome/pro-solid-svg-icons';

import AddLeadsToProjectModal from 'components/modals/add_leads_to_project_modal';
import LoadingSpinner from 'components/ui/loading_spinner';
import PageHeader from 'components/page_header';

import PeopleEmailFormModal from './modals/people_email_form_modal';
import PeopleDeleteGroupModal from './modals/people_delete_group_modal';
import PeopleBulkTasksModal from './modals/people_bulk_tasks_modal';
import PeopleBulkTaggingModal from './modals/people_bulk_tagging_modal';
import PeopleGroupFormModal from './modals/people_group_form_modal';
import PeopleBulkDeleteModal from './modals/people_bulk_delete_modal';
import PeopleAddToCallListModal from './modals/people_add_to_call_list_modal';
import PeopleNewCallListModal from './modals/people_new_call_list_modal';

import DuplicateContactsView from './duplicates/duplicate_contacts_view';
import PeopleActionsDropdown from './people_actions_dropdown';
import MobileContactFilters from '../filters/mobile_contact_filters';
import PeopleFilterTopbar from './people_filter_topbar';
import PeopleTable from './people_table';
import SvnHowToLink from './svn_how_to_link';

import { addLeadsToProject, endAddLeadsToProjects, retrieveGroups } from '../store/actions/people';
import { changePersonPage } from '../store/actions/query';
import { delayRetrieveRecords, downloadPeopleAsCsv, retrievePeople } from '../store/actions/search';
import { isDuplicateViewActive } from '../store/selectors/duplicates';

export class PeopleTab extends React.Component {
  static propTypes = {
    canExport: PropTypes.bool.isRequired,
    crmEmail: PropTypes.string,
    currentPage: PropTypes.number.isRequired,
    delayRetrieveRecords: PropTypes.func,
    downloadPeopleAsCsv: PropTypes.func.isRequired,
    isDuplicateViewActive: PropTypes.bool,
    isFetchingPeople: PropTypes.bool,
    leadStatuses: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired,
      status: PropTypes.string.isRequired
    })).isRequired,
    mobileFiltersVisible: PropTypes.bool.isRequired,
    newPersonPath: PropTypes.string.isRequired,
    pageSize: PropTypes.number.isRequired,
    people: PropTypes.array.isRequired,
    previewId: PropTypes.number,
    referralSources: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired,
      label: PropTypes.string.isRequired
    })),
    retrieveGroups: PropTypes.func.isRequired,
    retrievePeople: PropTypes.func.isRequired,
    selectedPersonIds: PropTypes.arrayOf(PropTypes.number).isRequired,
    startAddLeadsToProjects: PropTypes.bool.isRequired,
    totalCount: PropTypes.number.isRequired,
    totalPages: PropTypes.number.isRequired,

    onAddLeadsToProjects: PropTypes.func.isRequired,
    onCancelAddLeadsToProjects: PropTypes.func.isRequired,
    onPageChange: PropTypes.func.isRequired
  };

  scrollPanel = React.createRef();

  componentDidMount() {
    this.props.retrieveGroups();
  }

  handlePageChange = (page) => {
    this.props.onPageChange(page);
    this.props.retrievePeople();
    this.scrollToTop();
  };

  handleAddLeadsToProjects = (projectId, status) => {
    const { selectedPersonIds, onAddLeadsToProjects, onCancelAddLeadsToProjects } = this.props;
    onAddLeadsToProjects(projectId, selectedPersonIds, status);
    onCancelAddLeadsToProjects();
  }

  scrollToTop() {
    this.scrollPanel.current.scrollTop = 0;
  }

  render() {
    if (this.props.isDuplicateViewActive) {
      return <DuplicateContactsView />;
    }

    return (
      <>
        {this.renderPageHeader()}
        {this.renderFilterTopBar()}
        <div className="table-content">
          <div className="main-content" ref={this.scrollPanel}>
            {this.renderResults()}
            {this.renderPagination()}
          </div>
        </div>
        {this.renderMobileFilters()}
        {this.renderModals()}
      </>
    );
  }

  renderModals() {
    return (
      <React.Fragment>
        <PeopleBulkDeleteModal />
        <PeopleBulkTaggingModal />
        <PeopleBulkTasksModal />
        <PeopleDeleteGroupModal />
        <PeopleEmailFormModal />
        <PeopleGroupFormModal />
        <PeopleAddToCallListModal />
        <PeopleNewCallListModal />
        {this.renderAddLeadsToProjectModal()}
      </React.Fragment>
    );
  }

  renderMobileFilters() {
    if (!this.props.mobileFiltersVisible) return null;

    return (
      <div className="d-md-none mobile-content bg-light p-4">
        <MobileContactFilters />
      </div>
    );
  }

  renderFilterTopBar() {
    const {
      currentPage,
      pageSize,
      totalCount,
      people
    } = this.props;

    const pageData = {
      currentPage: currentPage,
      itemCount: people.length,
      itemSingularLabel: 'people',
      pageSize: pageSize,
      totalCount: totalCount
    };

    return (
      <PeopleFilterTopbar pageData={pageData} />
    );
  }

  renderPagination() {
    if (this.props.people.length < this.props.totalCount) {
      return (
        <div className="text-center">
          <UltimatePagination
            currentPage={this.props.currentPage}
            totalPages={this.props.totalPages}
            onChange={this.handlePageChange}
          />
        </div>
      );
    }
  }

  renderResults() {
    const {
      isFetchingPeople,
      people
    } = this.props;

    if (isFetchingPeople) {
      return <LoadingSpinner />;
    } else if (people.length > 0) {
      return (
        <PeopleTable />
      );
    } else {
      return (
        <div className="empty">
          <div className="empty-media">
            <FontAwesomeIcon className="text-muted mt-4"  icon={faSearch} size="5x"/>
          </div>
          <div className="empty-content">
            <h4 className="mt-4">
              <strong className="text-lg">No results found</strong>
            </h4>
            <p className="text-muted mb-4">Search with different criteria or create a New Contact</p>
            <SvnHowToLink className="mt-4" />
          </div>
        </div>
      );
    }
  }

  renderPageHeader() {
    const { newPersonPath } = this.props;

    return (
      <PageHeader
        iconDescription="Manage your contact database"
        iconName={faUsers}
        iconSize="lg"
        iconText="Contacts"
        iconType="icon"
      >
        <div className="d-flex align-items-center gap-2">
          <div className="d-none d-md-flex gap-2">
            <SvnHowToLink className="me-4" />
            <PeopleActionsDropdown />
            {this.renderCSVButton()}
          </div>
          <a className="btn btn-primary" href={newPersonPath}>
            New Contact
          </a>
        </div>
      </PageHeader>
    );
  }

  renderCSVButton() {
    if (this.props.canExport) {
      return (
        <button className="btn btn-secondary d-none d-md-inline-block" onClick={this.props.downloadPeopleAsCsv}>
          Export as CSV
        </button>
      );
    }
  }

  renderAddLeadsToProjectModal() {
    const {
      leadStatuses,
      referralSources,
      selectedPersonIds,
      startAddLeadsToProjects,
      onCancelAddLeadsToProjects
    } = this.props;

    return (
      <AddLeadsToProjectModal
        leadStatuses={leadStatuses}
        referralSources={referralSources}
        selectedContactCount={selectedPersonIds.length}
        shown={startAddLeadsToProjects}
        onCancel={onCancelAddLeadsToProjects}
        onSubmit={this.handleAddLeadsToProjects}
      />
    );
  }
}

const mapStateToProps = state => ({
  ...state.people,
  ...state.search.meta.people,
  ...state.static,
  canExport: state.ui.canExport,
  contactRoles: state.static.contactRoles,
  isDuplicateViewActive: isDuplicateViewActive(state),
  isFetchingPeople: state.search.isFetching,
  mobileFiltersVisible: state.ui.mobileFiltersVisible,
  newPersonPath: state.paths.people.newPerson,
  people: state.search.people,
  selectedPersonIds: state.selectList.selectedPersonIds
});

const mapDispatchToProps = {
  delayRetrieveRecords,
  downloadPeopleAsCsv,
  retrieveGroups,
  retrievePeople,

  onAddLeadsToProjects: addLeadsToProject,
  onCancelAddLeadsToProjects: endAddLeadsToProjects,
  onPageChange: changePersonPage
};

export default connect(mapStateToProps, mapDispatchToProps)(PeopleTab);
